import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import { FaArrowLeft, FaArrowRight, FaCalendarAlt, FaCalendarDay, FaHome, FaLanguage, FaMoon } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useRamadan } from "../context/RamadanContext";
import { useStartDate } from "../context/StartDateContext";
import DateChangerButton from "./DateChangerButton";

// Use a different approach for the icon component
const Icon = ({ icon, ...props }: { icon: IconType } & React.SVGProps<SVGSVGElement>) => {
  const IconComponent = icon as React.ElementType;
  return <IconComponent {...props} />;
};

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const { currentDay, setCurrentDay } = useRamadan();
  const { language, changeLanguage } = useLanguage();
  const { startDate, setStartDate } = useStartDate();
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    startDate ? startDate.toISOString().split('T')[0] : ''
  );

  // Handle day navigation
  const goToPreviousDay = () => {
    if (currentDay > 1) {
      setCurrentDay(currentDay - 1);
    }
  };

  const goToNextDay = () => {
    if (currentDay < 30) {
      setCurrentDay(currentDay + 1);
    }
  };

  // Handle language change
  const toggleLanguage = () => {
    changeLanguage(language === "bn" ? "en" : "bn");
  };

  // Handle date change modal
  const toggleDateModal = () => {
    setIsDateModalOpen(!isDateModalOpen);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const handleDateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDate) {
      const date = new Date(selectedDate);
      setStartDate(date);
      setIsDateModalOpen(false);
    }
  };

  return (
    <>
      {/* Desktop/Tablet Design - Only shown on medium screens and above */}
      <div className="hidden md:block bg-gradient-to-r from-[#f2edda] to-[#f7f5e8] rounded-xl shadow-md mb-4 overflow-hidden">
        {/* Top Section with Banner and Controls */}
        <div className="flex flex-col md:flex-row justify-between items-center p-3 relative">
          {/* Left Section with Day Display and Navigation */}
          <div className="flex items-center space-x-2 md:w-1/3">
            <button
              onClick={goToPreviousDay}
              disabled={currentDay <= 1}
              className={`p-2.5 rounded-full ${currentDay <= 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#e7f0dc] text-[#4b7a3c] hover:bg-[#a6ce86] hover:text-white transition-colors'}`}
              aria-label={t("navigation.previous")}
            >
              <Icon icon={FaArrowLeft} className="w-5 h-5" />
            </button>
            
            <div className="flex items-center bg-[#a6ce86] px-4 py-2 rounded-full">
              <div className="flex items-center justify-center bg-white rounded-full w-10 h-10 shadow-inner">
                <Icon icon={FaCalendarAlt} className="w-5 h-5 text-[#4b7a3c]" />
              </div>
              <div className="ml-2 text-white font-bengali font-bold">
                <span className="text-sm">{t("day.label")}</span>
                <span className="text-2xl ml-1">{t("day." + currentDay)}</span>
              </div>
            </div>
            
            <button
              onClick={goToNextDay}
              disabled={currentDay >= 30}
              className={`p-2.5 rounded-full ${currentDay >= 30 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#e7f0dc] text-[#4b7a3c] hover:bg-[#a6ce86] hover:text-white transition-colors'}`}
              aria-label={t("navigation.next")}
            >
              <Icon icon={FaArrowRight} className="w-5 h-5" />
            </button>
          </div>
          
          {/* Middle Banner */}
          <div className="flex flex-col items-center md:w-1/3 py-2">
            <div className="bg-[#c27260] text-white px-8 py-2.5 rounded-lg font-bengali font-bold text-2xl shadow-lg flex items-center">
              <Icon icon={FaMoon} className="w-6 h-6 mr-2" />
              <span>{t("day." + currentDay)} {t("header.ramadan")}</span>
            </div>
          </div>
          
          {/* Right Section with Logos and Controls */}
          <div className="flex items-center justify-end space-x-3 md:w-1/3 mt-3 md:mt-0">
            {/* Date Changer Button */}
            <div className="hidden sm:block">
              <DateChangerButton />
            </div>
            
            <button
              onClick={toggleLanguage}
              className="flex items-center px-3 py-2 text-sm bg-[#f2e6d9] text-[#8a744e] rounded-lg hover:bg-[#e8dab8] transition-colors shadow-sm"
            >
              <Icon icon={FaLanguage} className="w-5 h-5 mr-1" />
              {language === "bn" ? "English" : "বাংলা"}
            </button>
            
            {/* <Link
              to="/summary"
              className="p-2.5 bg-[#e7e5f9] text-[#616e9e] rounded-lg hover:bg-[#9eb1db] hover:text-white transition-colors shadow-sm flex items-center"
              aria-label={t("navigation.summary")}
            >
              <Icon icon={FaChartBar} className="w-5 h-5" />
              <span className="hidden lg:inline ml-1 font-medium">সারাংশ</span>
            </Link> */}
            
            <Link
              to="/"
              className="p-2.5 bg-[#e7f0dc] text-[#4b7a3c] rounded-lg hover:bg-[#a6ce86] hover:text-white transition-colors shadow-sm flex items-center"
              aria-label={t("navigation.home")}
            >
              <Icon icon={FaHome} className="w-5 h-5" />
              <span className="hidden lg:inline ml-1 font-medium">হোম</span>
            </Link>
          </div>
        </div>
        
        {/* Bottom Section - Decorative Bar */}
        <div className="h-2 bg-gradient-to-r from-[#a6ce86] via-[#c27260] to-[#9eb1db]"></div>
      </div>

      {/* Mobile Design - Only shown on small screens */}
      <div className="md:hidden z-10 mb-4">
        {/* Top Header with day information */}
        <div className="flex justify-center items-center px-2 py-3 bg-[#f7f5e8] rounded-t-lg border border-yellow-300 border-b-0 shadow-md">
          <div className="relative px-8">
            <div className="bg-[#c27260] text-white px-6 py-2 rounded-md font-bengali font-bold text-xl border-4 border-[#f7f5e8] shadow-lg">
              {currentDay} রামাদান
            </div>
            <button
              onClick={goToPreviousDay}
              disabled={currentDay <= 1}
              className={`absolute left-0 top-1/2 transform -translate-y-1/2 p-2 ${currentDay <= 1 ? 'text-gray-400' : 'text-[#4b7a3c]'}`}
              aria-label={t("navigation.previous")}
            >
              <Icon icon={FaArrowLeft} className="w-4 h-4" />
            </button>
            <button
              onClick={goToNextDay}
              disabled={currentDay >= 30}
              className={`absolute right-0 top-1/2 transform -translate-y-1/2 p-2 ${currentDay >= 30 ? 'text-gray-400' : 'text-[#4b7a3c]'}`}
              aria-label={t("navigation.next")}
            >
              <Icon icon={FaArrowRight} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Dock - Fixed at the bottom of the screen */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#f7f5e8] border-t border-yellow-300 shadow-lg z-50">
        <div className="flex justify-around items-center py-2">
          <Link
            to="/"
            className="flex flex-col items-center p-2 text-[#4b7a3c]"
            aria-label={t("navigation.home")}
          >
            <Icon icon={FaHome} className="w-5 h-5" />
            <span className="text-xs mt-1 font-bengali">{t("navigation.home")}</span>
          </Link>
          
          <button
            onClick={goToPreviousDay}
            disabled={currentDay <= 1}
            className={`flex flex-col items-center p-2 ${currentDay <= 1 ? 'text-gray-400' : 'text-[#4b7a3c]'}`}
            aria-label={t("navigation.previous")}
          >
            <Icon icon={FaArrowLeft} className="w-5 h-5" />
            <span className="text-xs mt-1 font-bengali">{t("navigation.prevDay")}</span>
          </button>
          
          <div className="flex flex-col items-center p-2 text-[#4b7a3c]">
            <Icon icon={FaCalendarAlt} className="w-5 h-5" />
            <span className="text-xs mt-1 font-bengali">{currentDay}</span>
          </div>
          
          <button
            onClick={goToNextDay}
            disabled={currentDay >= 30}
            className={`flex flex-col items-center p-2 ${currentDay >= 30 ? 'text-gray-400' : 'text-[#4b7a3c]'}`}
            aria-label={t("navigation.next")}
          >
            <Icon icon={FaArrowRight} className="w-5 h-5" />
            <span className="text-xs mt-1 font-bengali">{t("navigation.nextDay")}</span>
          </button>
          
          <button
            onClick={toggleDateModal}
            className="flex flex-col items-center p-2 text-[#4b7a3c]"
          >
            <Icon icon={FaCalendarDay} className="w-5 h-5" />
            <span className="text-xs mt-1 font-bengali">{t("dateChanger.setDate")}</span>
          </button>

          <button
            onClick={toggleLanguage}
            className="flex flex-col items-center p-2 text-[#4b7a3c]"
          >
            <Icon icon={FaLanguage} className="w-5 h-5" />
            <span className="text-xs mt-1">
              {language === "bn" ? "EN" : "বাংলা"}
            </span>
          </button>
        </div>
      </div>

      {/* Date Change Modal */}
      {isDateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={toggleDateModal}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {t('dateChanger.changeDate', 'Change Ramadan Start Date')}
                    </h3>
                    <div className="mt-4">
                      <form onSubmit={handleDateSubmit} className="space-y-6">
                        <div className="relative">
                          <label 
                            htmlFor="ramadan-start-date-change" 
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            {t('dateChanger.newStartDate', 'New start date')}
                          </label>
                          <input
                            type="date"
                            id="ramadan-start-date-change"
                            value={selectedDate}
                            onChange={handleDateChange}
                            className="block w-full px-4 py-3 text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                            required
                          />
                        </div>
                        
                        <div className="sm:flex sm:flex-row-reverse">
                          <button
                            type="submit"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                          >
                            {t('dateChanger.save', 'Save Changes')}
                          </button>
                          <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={toggleDateModal}
                          >
                            {t('dateChanger.cancel', 'Cancel')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
